import { z } from 'zod';

/**
 * What a project history event records (DEV-3760). Callers pass it along with
 * the update that makes the change; see {@link ProjectEvent}.
 */
export const projectEventActivitySchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('proposal-created'), proposalId: z.string() }),
  z.object({ kind: z.literal('version-created'), versionId: z.string() }),
  z.object({ kind: z.literal('action-created'), actionId: z.string() }),
  z.object({ kind: z.literal('baseline-edited'), versionId: z.string() }),
  z.object({
    kind: z.literal('storey-footprint-edited'),
    buildingId: z.string(),
    /** First edited storey; the only one on events recorded per storey. */
    storeyId: z.string(),
    /**
     * Every storey edited, when more than one: one event stands for a whole
     * burst of edits to the building (DEV-3805).
     */
    storeyIds: z.array(z.string()).optional(),
  }),
  z.object({
    kind: z.literal('storey-added'),
    buildingId: z.string(),
    /**
     * The storey that was added, so edits to it in the same clock group fold
     * into this row. Missing on events recorded before it was stored.
     */
    storeyId: z.string().optional(),
  }),
  z.object({ kind: z.literal('building-moved'), buildingId: z.string() }),
  z.object({ kind: z.literal('building-created'), buildingId: z.string() }),
  z.object({
    kind: z.literal('building-deleted'),
    buildingId: z.string(),
    /** Display name at deletion, since the building no longer exists. */
    name: z.string(),
  }),
  z.object({ kind: z.literal('project-location') }),
  z.object({
    kind: z.literal('proposal-deleted'),
    proposalId: z.string(),
    name: z.string(),
  }),
  z.object({
    kind: z.literal('version-deleted'),
    versionId: z.string(),
    name: z.string(),
  }),
]);
export type ProjectEventActivity = z.infer<typeof projectEventActivitySchema>;
export type ProjectEventKind = ProjectEventActivity['kind'];

/** Who decided the change: a user, or the AI agent acting for a user. */
export const projectEventActorKindSchema = z.enum(['user', 'agent']);
export type ProjectEventActorKind = z.infer<typeof projectEventActorKindSchema>;

/** How the change reached the app: the UI, Nodon's agent chat, or an external client over MCP. */
export const projectEventChannelSchema = z.enum(['ui', 'mcp', 'agent-chat']);
export type ProjectEventChannel = z.infer<typeof projectEventChannelSchema>;

/**
 * One entry in the project history, stored on {@link Project.events} so it
 * saves, undoes and syncs together with the change it describes. Whoever
 * makes the change passes the activity along with it: the UI from the user's
 * action, the agent chat and MCP from their tools.
 */
export const projectEventSchema = z.object({
  id: z.string(),
  /** User the change belongs to. For agent events, the user who asked. */
  actor_id: z.string(),
  actor_kind: projectEventActorKindSchema,
  channel: projectEventChannelSchema,
  activity: projectEventActivitySchema,
  /** Time recorded when the event was appended. */
  created_at: z.string(),
  /**
   * Time a later edit was last merged into this event (DEV-3805), so the
   * merged edit still counts as unread. Missing when nothing was merged.
   */
  updated_at: z.string().optional(),
});
export type ProjectEvent = z.infer<typeof projectEventSchema>;

/** Who records an event and through which channel. */
export type ProjectEventActor = Pick<
  ProjectEvent,
  'actor_id' | 'actor_kind' | 'channel'
>;

/**
 * Repeats by the same user closer than this collapse into one thread entry
 * (project location, a deletion right after a creation). Same gap as the one
 * that starts a new clock group in the comments thread
 * (`shouldShowChatTimestamp`).
 */
export const PROJECT_EVENT_CLOCK_GROUP_MS = 30 * 60 * 1000;
